/* eslint-disable jsx-a11y/role-supports-aria-props */
import {
	Assignment,
	CardTravel,
	Category,
	ContactPhone,
	Dashboard,
	Equalizer,
	ExitToApp,
	Group,
	LocalOffer,
	MailOutline,
	Memory,
	NotificationsNone,
	Store,
	Straighten,
	Settings,
	ViewCompact,
	ViewModule,
} from '@material-ui/icons'
import React, { useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { NavLink } from 'react-router-dom'
import { ROLES } from '../../../../../utils/constants'
import { checkIsActive } from '../../../../_helpers'
import './AsideMenuList.css'

function isItemRouteActive(location, item) {
	return Boolean(item.path && checkIsActive(location, item.path)
		&& !item.excludePaths?.some((path) => checkIsActive(location, path)))
}

function isItemActive(location, item) {
	if (isItemRouteActive(location, item))
		return true


	if (!item.children?.length)
		return false


	return item.children.some((child) => isItemActive(location, child))
}

function renderMenuItems({ items, location, openMenus, setOpenMenus, forceOpenSubmenus = false }) {
	return items.map((item) => {
		const isSubmenuItem = item.level > 0

		if (item.type === 'section')
			return (
				<li className="menu-section dalibith-menu-section" key={item.id}>
					<h4 className="menu-text">{item.label}</h4>
					<i className="menu-icon ki ki-bold-more-hor icon-md"></i>
				</li>
			)


		if (item.type === 'separator')
			return <li className="menu-section" key={item.id}></li>


		const hasChildren = Boolean(item.children?.length)
		const selfActive = isItemRouteActive(location, item)
		const active = isItemActive(location, item)
		// Al entrar directamente en un catálogo, se muestra su ubicación.
		// Una elección explícita del usuario sigue permitiendo plegar el menú.
		const open = hasChildren && (forceOpenSubmenus || (openMenus[item.id] ?? active))
		const itemClasses = [
			'menu-item',
			hasChildren ? 'menu-item-submenu' : '',
			open ? 'menu-item-open menu-item-not-hightlighted' : '',
			selfActive ? 'dalibith-menu-item-active' : '',
		]
			.filter(Boolean)
			.join(' ')

		const iconClasses = [
			'menu-icon',
			'dalibith-menu-icon',
			selfActive ? 'dalibith-menu-icon-active' : '',
		]
			.filter(Boolean)
			.join(' ')
		const textClasses = [
			'menu-text',
			'dalibith-menu-text',
			selfActive ? 'dalibith-menu-text-active' : '',
		]
			.filter(Boolean)
			.join(' ')
		const rowClasses = [
			'menu-link',
			'dalibith-menu-row',
			isSubmenuItem ? 'dalibith-menu-row-submenu' : '',
			item.indented ? 'dalibith-menu-row-inset' : '',
			hasChildren && forceOpenSubmenus ? 'dalibith-menu-row-fixed-open' : '',
			selfActive ? 'dalibith-menu-row-active' : '',
		]
			.filter(Boolean)
			.join(' ')
		const labelClasses = [
			'dalibith-menu-label',
			isSubmenuItem ? 'dalibith-menu-label-submenu' : '',
		]
			.filter(Boolean)
			.join(' ')
		const arrowClasses = [
			'menu-arrow',
			'dalibith-menu-arrow',
			selfActive ? 'dalibith-menu-arrow-active' : '',
		]
			.filter(Boolean)
			.join(' ')
		const toggleSubmenu = () =>
			setOpenMenus((current) => ({
				...current,
				[item.id]: !open,
			}))

		const icon = item.icon ? (
			<span className={iconClasses}>{item.icon}</span>
		) : null

		const labelContent = item.path ? (
			<NavLink
				className={labelClasses}
				to={item.path}
				isActive={() => selfActive}
			>
				{icon}
				<span className={textClasses}>{item.label}</span>
			</NavLink>
		) : (
			<div
				className={`${labelClasses} ${hasChildren ? '' : 'dalibith-menu-label-disabled'}`.trim()}
				role={hasChildren ? 'button' : undefined}
				tabIndex={hasChildren ? 0 : undefined}
				aria-expanded={hasChildren ? open : undefined}
				onClick={hasChildren ? toggleSubmenu : undefined}
				onKeyDown={hasChildren ? (event) => {
					if (event.key === 'Enter' || event.key === ' ') {
						event.preventDefault()
						toggleSubmenu()
					}
				} : undefined}
			>
				{icon}
				<span className={textClasses}>{item.label}</span>
			</div>
		)

		return (
			<li className={itemClasses} aria-haspopup="true" key={item.id}>
				<div
					className={rowClasses}
				>
					{labelContent}
					{hasChildren && !forceOpenSubmenus && (
						<span
							className="dalibith-submenu-indicator"
							onClick={(event) => {
								event.preventDefault()
								event.stopPropagation()
								toggleSubmenu()
							}}
							onKeyDown={(event) => {
								if (event.key === 'Enter' || event.key === ' ') {
									event.preventDefault()
									toggleSubmenu()
								}
							}}
							role="button"
							tabIndex={0}
							aria-label={`${open ? 'Cerrar' : 'Abrir'} ${item.label}`}
							aria-expanded={open}
						>
							<i className={arrowClasses}></i>
						</span>
					)}
				</div>

				{hasChildren && open && (
					<div className="menu-submenu">
						<i className="menu-arrow dalibith-menu-arrow"></i>
						<ul className="menu-subnav dalibith-menu-subnav">
							{renderMenuItems({
								items: item.children.map((child) => ({
									...child,
									level: (item.level ?? 0) + 1,
								})),
								location,
								openMenus,
								setOpenMenus,
								forceOpenSubmenus,
							})}
						</ul>
					</div>
				)}
			</li>
		)
	})
}

export function AsideMenuList({ layoutProps }) {
	const loggedUser = useSelector(
		(store) => store.authentication?.user,
		shallowEqual
	)
	const location = useLocation()
	const [openMenus, setOpenMenus] = useState({})

	const adminAccess =
		loggedUser?.role?.rango === ROLES.SUPER_ADMIN ||
		loggedUser?.role?.rango === ROLES.ADMIN
	const offerManagerAccess =
		loggedUser?.role?.rango === ROLES.GESTOR_OFERTAS

	const offerItems = [
		{ id: 'offers-section', type: 'section', label: 'Ofertas' },
		{
			id: 'ofertas-listado',
			label: 'Listado de ofertas',
			icon: <LocalOffer />,
			path: '/ofertas',
			excludePaths: ['/ofertas/importaciones'],
		},
		{
			id: 'importaciones-ofertas',
			label: 'Importaciones',
			icon: <Assignment />,
			path: '/ofertas/importaciones',
		},
		{
			id: 'configuracion-catalogos-ofertas',
			label: 'Presupuestos',
			icon: <Settings />,
			children: [
				{ id: 'partidas', label: 'Partidas', icon: <CardTravel />, path: '/partidas' },
				{ id: 'capitulos', label: 'Categorías de partidas', icon: <ViewModule />, path: '/categorias-partidas', indented: true },
				{ id: 'productos', label: 'Productos', icon: <Dashboard />, path: '/productos' },
				{ id: 'familias-productos', label: 'Familias', icon: <Category />, path: '/familias-productos', indented: true },
				...(adminAccess ? [{ id: 'tipos-unidades-productos', label: 'Tipologías', icon: <Straighten />, path: '/tipos-unidades-productos', indented: true }] : []),
				{ id: 'marcas-productos', label: 'Marcas', icon: <ViewCompact />, path: '/marcas-productos', indented: true },
				{ id: 'agrupaciones-materiales', label: 'Agrupaciones de materiales', icon: <ViewModule />, path: '/agrupaciones-materiales' },
			],
		},
	]

	const commercialItems = offerManagerAccess
		? offerItems
		: [
			{ id: 'dashboard', label: 'Visión Global', icon: <Equalizer />, path: '/dashboard' },
			...offerItems,
			{ id: 'management-section', type: 'section', label: 'Gestión' },
			{ id: 'obras', label: 'Obras', icon: <Assignment />, path: '/obras' },
			{ id: 'clientes', label: 'Clientes', icon: <ContactPhone />, path: '/clientes' },
			{
				id: 'proveedores', label: 'Proveedores', icon: <Store />, path: '/proveedores',
				children: [
					{ id: 'tipos-proveedores', label: 'Tipos de proveedores', icon: <ViewCompact />, path: '/tipos-proveedores' },
				],
			},
		]

	const administrationItems = adminAccess
		? [
			{ id: 'administration-section', type: 'section', label: 'Administración' },
			{
				id: 'usuarios',
				label: 'Usuarios',
				icon: <Group />,
				path: '/usuarios',
			},
			{
				id: 'operarios',
				label: 'Operarios',
				icon: <Group />,
				path: '/operarios',
				children: [
					{
						id: 'categorias-operarios',
						label: 'Categorías de operario',
						icon: <ViewCompact />,
						path: '/categorias-operarios',
					},
				],
			},
			{
				id: 'configuracion',
				label: 'Configuración',
				icon: <Settings />,
				children: [
					{
						id: 'configuracion-general',
						label: 'General',
						icon: <Settings />,
						path: '/configuracion/general',
					},
					{
						id: 'configuracion-ia',
						label: 'IA',
						icon: <Memory />,
						path: '/configuracion/ia',
					},
					{
						id: 'configuracion-mail',
						label: 'Mail',
						icon: <MailOutline />,
						path: '/configuracion/mail',
					},
					{
						id: 'configuracion-mensajeria',
						label: 'Mensajería',
						icon: <NotificationsNone />,
						path: '/configuracion/mensajeria',
					},
				],
			},
		]
		: []

	const footerItems = [
		{ id: 'footer-separator', type: 'separator' },
		{
			id: 'logout',
			label: 'Logout',
			icon: <ExitToApp />,
			path: '/logout',
			style: { backgroundColor: '#ff0000' },
		},
	]

	const items = [...commercialItems, ...administrationItems, ...footerItems]

	return (
		<>
			<ul className={`menu-nav ${layoutProps.ulClasses} dalibith-aside-menu`}>
				{renderMenuItems({
					items: items.map((item) => ({ ...item, level: 0 })),
					location,
					openMenus,
					setOpenMenus,
					forceOpenSubmenus: false,
				})}
			</ul>
		</>
	)
}
